import {
  docElectronicoIdFromPublicId,
  formatFechaHoraRegistroPdf,
} from '../infrastructure/foGesrFieldFormattingCore.ts';
import {
  drawFoGesrHeaderTable,
  FO_GESR_SPEC_REDENCION_BONO,
  pintarPiePaginas,
} from '../infrastructure/foGesrPdfLayout.ts';
import {
  createFoGesrLandscapeA4Base,
  makeFoGesrCellLines,
  scaleTableColumnMm,
  writeFoGesrAvisoPrivacidadBlock,
  writeFoGesrMapUbicacionBlock,
  writeFoGesrMetaLabelValuePairs,
} from '../infrastructure/foGesrReportPdfBuildCore.ts';
import type { RedencionBonoReportPdfInput } from './redencionBonoReportPdfData.ts';
import {
  drawRedencionBannerRow,
  drawRedencionParticipantRow,
  drawRedencionTextRow,
  type RedencionTableDrawContext,
} from './redencionBonoReportTableDrawing.ts';

export { triggerPdfDownload } from '../infrastructure/foGesrPdfLayout.ts';
export type { RedencionBonoReportPdfInput, RedencionBonoReportPdfRow } from './redencionBonoReportPdfData.ts';

const TABLA_ENCABEZADOS: string[] = [
  'NÚMERO DE\nBONO',
  'TIPO DE BONO',
  'DOCUMENTO\nPARTICIPANTE',
  'NOMBRE DEL\nPARTICIPANTE',
  'NOMBRE DE LA PERSONA\nAUTORIZADA',
  '¿AUTORIZA POLÍTICA\nDE PRIVACIDAD?',
  'REGISTRO\nFOTOGRÁFICO',
  'FIRMA\nRECIBIDO',
];

const COL_WIDTHS_MM = [14, 22, 24, 34, 40, 22, 25, 25];

/**
 * Generates official **FO-GESR-1775** redemption vouchers (Spanish welfare bonus layout).
 */
export async function buildRedencionBonoReportPdf(input: RedencionBonoReportPdfInput): Promise<Blob> {
  const { doc, pageW, pageH, margin, usableW, y, setNormal, setBold, newPage: newPageContinuacion } =
    await createFoGesrLandscapeA4Base();

  const colW = scaleTableColumnMm(COL_WIDTHS_MM, usableW);
  const lh = 2.65;
  const pad = 0.7;
  const cellLines = makeFoGesrCellLines(doc, colW, pad);

  const tableCtx: RedencionTableDrawContext = {
    doc,
    y,
    margin,
    pageH,
    w: colW,
    lh,
    pad,
    newPage: newPageContinuacion,
    setNormal,
    setBold,
    cellLines,
  };

  y.current = await drawFoGesrHeaderTable(doc, y.current, margin, usableW, FO_GESR_SPEC_REDENCION_BONO);
  y.current += 5;

  const fechaReg = formatFechaHoraRegistroPdf(input.fechaRegistroRaw);

  const metaPairs: [string, string][] = [
    ['PROVEEDOR:', input.proveedorLine],
    ['RESPONSABLE:', input.responsable],
    ['FECHA DE REGISTRO:', fechaReg],
    ['GRUPO DE ALIMENTOS:', input.grupoAlimentos],
    ['ENTREGA N°:', input.entregaN],
    ['REMANENTE:', input.remanente],
  ];
  writeFoGesrMetaLabelValuePairs(
    { doc, y, margin, pageH, usableW, setNormal, setBold, onNewPage: newPageContinuacion },
    metaPairs,
    'bold',
  );

  writeFoGesrAvisoPrivacidadBlock(doc, y, margin, pageH, usableW, setNormal, newPageContinuacion);

  drawRedencionBannerRow(tableCtx, 'REDENCIÓN');
  drawRedencionTextRow(tableCtx, TABLA_ENCABEZADOS, true);

  if (input.rows.length === 0) {
    await drawRedencionParticipantRow(
      tableCtx,
      ['—', '—', '—', '—', '—', '—', '', ''],
      '',
      '',
    );
  } else {
    for (const r of input.rows) {
      await drawRedencionParticipantRow(
        tableCtx,
        [
          r.bono,
          r.tipoBono,
          r.documentoParticipante,
          r.nombreParticipante,
          r.nombrePersonaAutorizada,
          r.autorizaPrivacidad,
          '',
          '',
        ],
        r.registroFotografico,
        r.firma,
      );
    }
  }

  await writeFoGesrMapUbicacionBlock(
    { doc, y, margin, pageH, usableW, setBold, setNormal, onNewPage: newPageContinuacion },
    {
      lat: input.latitud ?? '',
      lon: input.longitud ?? '',
      ubicacionDetalle: input.ubicacionTexto,
      mapTitle: 'UBICACIÓN:',
    },
  );

  setBold(8);
  doc.text('Documento Electrónico', margin, y.current);
  y.current += 4;
  setNormal(8);
  doc.text(`ID: ${docElectronicoIdFromPublicId(input.docPublicId)}`, margin, y.current);

  pintarPiePaginas(doc, pageW, pageH);

  return await doc.output('blob');
}
